import React from "react";
import { type FC, type PropsWithChildren, useCallback } from "react";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { Button } from "@/components/flexkit/Button";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { Check, Copy } from "lucide-react";
import { Tooltip } from "@/components/Tooltip";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";

type MarkdownCopyablePreProps = PropsWithClassName & PropsWithChildren;

export const MarkdownCopyablePre: FC<MarkdownCopyablePreProps> = ({ children, className, ...props }) => {
    const [copied, setCopied] = useTemporaryState(false, 2000);

    const handleCopyToClipboard = useCallback(async () => {
        const textContent = typeof children === "string" ? children : extractTextFromChildren(children);
        try {
            await navigator.clipboard.writeText(textContent);
            setCopied(true);
        } catch (error) {
            setCopied(false);
            toast({
                title: "Failed to copy to clipboard",
                description: formatError(error),
            });
        }
    }, [setCopied, children]);

    return (
        <div className="relative">
            <pre className={cn("peer", className)} {...props}>
                <ScrollArea orientation="horizontal">{children}</ScrollArea>
            </pre>
            <div className="absolute right-2 top-2 opacity-0 transition-opacity focus-within:opacity-100 hover:opacity-100 peer-hover:opacity-100">
                <Tooltip content="Copy to clipboard">
                    <Button
                        variant="ghost"
                        type="button"
                        size={"xs"}
                        LeadingIcon={() =>
                            copied ? <Check className="text-content-success" size={16} /> : <Copy size={16} />
                        }
                        onClick={handleCopyToClipboard}
                        aria-label="Copy to clipboard"
                        title="Copy to clipboard"
                        data-tracking-id="copy-to-clipboard-markdown"
                    />
                </Tooltip>
            </div>
        </div>
    );
};

// Helper function to extract text content from React children
const extractTextFromChildren = (children: React.ReactNode): string => {
    if (typeof children === "string") {
        return children;
    }

    if (Array.isArray(children)) {
        return children.map(extractTextFromChildren).join("");
    }

    if (React.isValidElement(children) && Boolean(children.props.children)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return extractTextFromChildren(children.props.children);
    }

    return "";
};
